import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { Field } from "@/components/ui/Field";
import { Button } from "@/components/ui/Button";
import { ExternalLinkIcon, PlugIcon, XIcon } from "@/lib/icons";

export function ConnectTwilioPage() {
  const navigate = useNavigate();
  const ctx = useConnectionContext();
  const [accountSid, setAccountSid] = useState("");
  const [apiKeySid, setApiKeySid] = useState("");
  const [apiKeySecret, setApiKeySecret] = useState("");
  const [friendlyName, setFriendlyName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.twilio.connect({
        accountSid,
        apiKeySid,
        apiKeySecret,
        friendlyName,
      });
      // Pull the new connection into the global account-context cache before
      // we navigate so the dashboard reflects it on first paint.
      await ctx.refresh();
      navigate("/");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Failed to connect");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <header>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Onboarding · Step 1 of 1
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Connect a Twilio account
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            We authenticate with a Twilio API Key — not your master Auth Token. Use a{" "}
            <strong className="font-semibold text-zinc-800 dark:text-zinc-200">
              Restricted (read-only)
            </strong>{" "}
            key for the smallest blast radius.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-zinc-200 bg-white p-6 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-card dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <Field
            label="Account SID"
            required
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            className="font-mono tracking-tight"
            autoComplete="off"
            value={accountSid}
            onChange={(e) => setAccountSid(e.target.value)}
          />
          <Field
            label="API Key SID"
            required
            placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            className="font-mono tracking-tight"
            autoComplete="off"
            value={apiKeySid}
            onChange={(e) => setApiKeySid(e.target.value)}
          />
          <Field
            label="API Key Secret"
            type="password"
            required
            placeholder="••••••••••••••••••••••••••••••••"
            className="font-mono tracking-tight"
            autoComplete="off"
            value={apiKeySecret}
            onChange={(e) => setApiKeySecret(e.target.value)}
          />
          <Field
            label="Friendly name"
            hint="Optional · How this account shows up in your dashboard."
            placeholder="Production"
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
          />
          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" type="button" onClick={() => navigate("/")}>
              <XIcon size={14} />
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              <PlugIcon size={14} />
              {busy ? "Connecting…" : "Connect account"}
            </Button>
          </div>
        </form>
      </div>

      <aside className="space-y-3">
        <InfoCard
          title="How to create the API Key"
          description={
            <>
              In Twilio Console, go to{" "}
              <a
                href="https://console.twilio.com/us1/account/keys-credentials/api-keys"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
              >
                Account → API keys
                <ExternalLinkIcon size={12} />
              </a>
              , click <em>Create API key</em>, choose <em>Restricted</em>, and grant{" "}
              <em>read-only</em> access to Messages, Calls, and Usage Records.
            </>
          }
        />
        <InfoCard
          title="Encrypted at rest"
          description="The API Key Secret is sealed with AES-256-GCM the moment it hits our servers. Plaintext never touches disk."
        />
        <InfoCard
          title="Revocable any time"
          description="Rotate or revoke the key from Twilio Console without affecting your master account or other integrations."
        />
        <InfoCard
          title="Real-time ingestion"
          description="Insights appear in seconds. Historical backfill runs in parallel — no waiting for tomorrow's batch."
        />
      </aside>
    </div>
  );
}

function InfoCard({ title, description }: { title: string; description: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/30 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <div className="text-[12.5px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</div>
      <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-600 dark:text-zinc-400">
        {description}
      </p>
    </div>
  );
}
